import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { EditTwoTone, PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Col,
  Descriptions,
  Divider,
  Modal,
  notification,
  Popover,
  Row,
  Space,
  Typography,
} from "antd";

import DataTable from "components/elements/DataTable";
import {
  ColumnType,
  FiltersValues,
} from "components/elements/DataTable/DataTable";

import {
  useActivateThsQuestionnaireMutation,
  useDeleteThsMutation,
  useGetTreuhaenderMasterdataForTreuhandstelleQuery,
  useSetThsDeclinedMutation,
} from "store/masterdata";
import { useChangeTreuhandstellePreferencesMutation } from "store/datacontrol";
import { LandwirtMasterdata, Treuhandstelle } from "types/masterdata.types";
import useEnumsFilters from "hooks/useEnumsFilters";
import THInfoModal from "views/App/views/Masterdata/components/THInfoModal";
import { NumberParam, useQueryParams } from "use-query-params";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import ContactTreuhaenderModal from "views/App/views/Masterdata/components/THsInfoModal/ContactTreuhaenderModal";
import ConfirmationModal from "views/App/components/Modals/ConfirmationModal/ConfirmationModal";
import {
  useSendStatusUpdateMailMutation,
  useSendThsAkbMutation,
} from "store/dispatch/dispatch.api";
import getAuthority from "utils/getAuthority";
import { Roles } from "types/user.types";
import { isNil } from "lodash";
import UpdateTHsForm from "./Forms/UpdateTHsForm";
import AddTHForm from "./Forms/AddTHForm";
import styles from "./THsInfoModal.module.less";

interface Props {
  treuhandstelleData: Treuhandstelle;
  rekStatusFilters: FiltersValues;
  visible: boolean;
  onClose(clearQuery?: boolean): void;
  refetchTreuhandstelleData(): void;
}

const { Title } = Typography;

function THsInfoModal({
  refetchTreuhandstelleData,
  treuhandstelleData,
  rekStatusFilters,
  visible,
  onClose,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const [, setQuery] = useQueryParams({
    kundennummer: NumberParam,
  });

  const [tHInfoModalProps, setTHInfoModalProps] = useState({
    visible: false,
    treuhaenderId: -1,
    kundennummer: -1,
  });
  const [tHsFormVisible, setTHsFormVisible] = useState(false);
  const [addTHFormVisible, setAddTHFormVisible] = useState(false);
  const [useMailOnly, setUseMailOnly] = useState(false);
  const [useThsMailForEmployees, setUseThsMailForEmployees] = useState(false);
  const [useAllowDuplicateIban, setUseAllowDuplicateIban] = useState(false);
  const [changeTreuhandstellePreferences] =
    useChangeTreuhandstellePreferencesMutation();
  const [contactTreuhaenderModalVisible, setContactTreuhaenderModalVisible] =
    useState(false);
  const { getOnFilter } = useEnumsFilters<
    LandwirtMasterdata & { id: number }
  >();
  const [confirmationModalVisible, setConfirmationModalVisible] =
    useState(false);
  const [
    thsDeclinedConfirmationModalVisible,
    setThsDeclinedConfirmationModalVisible,
  ] = useState(false);
  const [sendAkbConfirmationModalVisible, setSendAkbConfirmationModalVisible] =
    useState(false);
  const [
    activateThsQuestionnaireModalVisible,
    setActivateThsQuestionnaireModalVisible,
  ] = useState(false);
  const [
    deleteThsConfirmationModalVisible,
    setDeleteThsConfirmationModalVisible,
  ] = useState(false);
  const [sendStatusMail] = useSendStatusUpdateMailMutation();
  const [setThsDelined] = useSetThsDeclinedMutation();
  const [deleteThs] = useDeleteThsMutation();
  const [activateThsQuestionnaireCall] = useActivateThsQuestionnaireMutation();
  const [sendThsAkb] = useSendThsAkbMutation();

  const roles = getAuthority();

  const actionsVisible = roles.some(
    (role) =>
      role === Roles.LinkPL ||
      role === Roles.LinkFeldleitung ||
      role === Roles.LinkAdmin
  );

  const questionnaireLinkVisible = roles.some(
    (role) =>
      role === Roles.LinkPL ||
      role === Roles.LinkFeldleitung ||
      role === Roles.LinkAdmin ||
      role === Roles.LinkAgentPlus
  );

  useEffect(() => {
    setUseMailOnly(treuhandstelleData.useMailOnly);
    setUseThsMailForEmployees(treuhandstelleData.useThsMailForEmployees);
    setUseAllowDuplicateIban(treuhandstelleData.allowDuplicateIban);
  }, [treuhandstelleData]);

  const handleChangeThsPreferences = async (values: {
    useMailOnly: boolean;
    useThsMailForEmployees: boolean;
    useAllowDuplicateIban: boolean;
    contactTreuhaenderId?: number;
  }) => {
    try {
      const payload = await changeTreuhandstellePreferences({
        treuhandstelleId: treuhandstelleData.id,
        ...values,
      }).unwrap();
      if (!payload.hasErrors) {
        refetchTreuhandstelleData();
        notification.success({
          message: t("data_control.change_ths_preferences_success"),
        });
      } else {
        notification.error({ message: payload.errorDetails.errorMessage });
      }
    } catch (error) {
      console.error("Error", error);
    }
  };

  const { data, isLoading, isFetching, refetch } =
    useGetTreuhaenderMasterdataForTreuhandstelleQuery({
      treuhandstelleId: treuhandstelleData.id,
    });

  const handleUseMailOnly = (e: CheckboxChangeEvent) => {
    if (!e.target.checked) {
      setUseMailOnly(e.target.checked);
      handleChangeThsPreferences({
        useMailOnly: e.target.checked,
        useThsMailForEmployees,
        useAllowDuplicateIban,
      });
    } else {
      setContactTreuhaenderModalVisible(true);
    }
  };

  const openTHsFormModal = () => {
    setTHsFormVisible(true);
  };

  const openAddTHFormModal = () => {
    setAddTHFormVisible(true);
  };

  const openStatusMailConfirmationModal = () => {
    setConfirmationModalVisible(true);
  };

  const openThsDeclinedConfirmationModal = () => {
    setThsDeclinedConfirmationModalVisible(true);
  };

  const openSendAkbConfirmationModal = () => {
    setSendAkbConfirmationModalVisible(true);
  };

  const openDeleteThsConfirmationModal = () => {
    setDeleteThsConfirmationModalVisible(true);
  };

  const handleTHNummerClick = (treuhaenderId: number, kundennummer: number) => {
    setTHInfoModalProps({ visible: true, treuhaenderId, kundennummer });
  };

  const goToLWDetailsModal = (kundennummer: number) => {
    setQuery({ kundennummer });
    onClose();
  };

  const confirmSendStatusMail = async () => {
    try {
      const response = await sendStatusMail({
        treuhandstelleId: treuhandstelleData.id,
      }).unwrap();
      if (!response.hasErrors) {
        notification.success({
          message: t("data_control.statusversand_success"),
        });
      } else {
        notification.error({ message: response.errorMessage });
      }
    } catch (error) {
      console.error("Error", error);
    } finally {
      setConfirmationModalVisible(false);
    }
  };

  const confirmSetThsDeclined = async () => {
    try {
      const response = await setThsDelined({
        treuhandstelleId: treuhandstelleData.id,
      }).unwrap();
      if (!response.hasErrors) {
        refetchTreuhandstelleData();
        refetch();
        notification.success({
          message: t("data_control.ths_declined_succcess"),
        });
      } else {
        notification.error({ message: response.errorMessage });
      }
    } catch (error) {
      console.error("Error", error);
    } finally {
      setThsDeclinedConfirmationModalVisible(false);
    }
  };

  const confirmSendAkb = async () => {
    try {
      const response = await sendThsAkb({
        treuhandstelleId: treuhandstelleData.id,
      }).unwrap();
      if (!response.hasErrors) {
        notification.success({
          message: t("data_control.send_akb_letter_success"),
        });
      } else {
        notification.error({ message: response.errorMessage });
      }
    } catch (error) {
      console.error("Error", error);
    } finally {
      setSendAkbConfirmationModalVisible(false);
    }
  };

  const activateThsQuestionnaire = async () => {
    try {
      const response = await activateThsQuestionnaireCall({
        treuhandstelleId: treuhandstelleData.id,
      }).unwrap();
      if (!response.hasErrors) {
        refetchTreuhandstelleData();
        refetch();
        notification.success({
          message: t("data_control.activate_ths_questionnaire_success"),
        });
      } else {
        notification.error({ message: response.errorMessage });
      }
    } catch (error) {
      console.error("Error", error);
    } finally {
      setActivateThsQuestionnaireModalVisible(false);
    }
  };

  const confirmDeleteThs = async () => {
    try {
      const response = await deleteThs({
        treuhandstelleId: treuhandstelleData.id,
      }).unwrap();
      if (!response.hasErrors) {
        onClose(false);
        notification.success({
          message: t("data_control.delete_ths_succcess"),
        });
      } else {
        notification.error({ message: response.errorMessage });
      }
    } catch (error) {
      console.error("Error", error);
    } finally {
      setDeleteThsConfirmationModalVisible(false);
    }
  };

  function formatToStringEmptyIfNoKundennummer(
    value: string,
    row: LandwirtMasterdata | undefined
  ): JSX.Element {
    return row && row.kundennummer > 0 ? <span>{value}</span> : <span />;
  }

  return (
    <Modal
      title={
        <Title level={4} className={styles.title}>
          {t("data_control.ths_title").replace(
            "{0}",
            treuhandstelleData?.extId?.toString()
          )}
        </Title>
      }
      visible={visible}
      footer={null}
      onCancel={() => onClose(true)}
      destroyOnClose
      width={1300}
    >
      <Row gutter={[10, 10]}>
        <Col className="gutter-row">
          <Button
            type="primary"
            onClick={openStatusMailConfirmationModal}
            disabled={
              !treuhandstelleData.contactTreuhaenderId ||
              !treuhandstelleData.useMailOnly
            }
          >
            {t("data_control.statusversand")}
          </Button>
        </Col>
        <Col className="gutter-row">
          <Button
            icon={<PlusOutlined />}
            type="primary"
            onClick={openAddTHFormModal}
          >
            {t("data_control.add_th")}
          </Button>
        </Col>
        {actionsVisible && (
          <Col className="gutter-row">
            <Button
              type="primary"
              onClick={openThsDeclinedConfirmationModal}
              disabled={treuhandstelleData.declinedParticipation}
            >
              {t("data_control.ths_declined")}
            </Button>
          </Col>
        )}
        {actionsVisible && (
          <Col className="gutter-row">
            <Button
              type="primary"
              onClick={openDeleteThsConfirmationModal}
              disabled={data?.some((x) => x.kundennummer > 0)}
            >
              {t("data_control.delete_ths")}
            </Button>
          </Col>
        )}
        {actionsVisible && (
          <Col className="gutter-row">
            <Button
              type="primary"
              onClick={() => setActivateThsQuestionnaireModalVisible(true)}
              disabled={!data?.some((x) => x.rekStatus === 34)}
            >
              {t("data_control.activate_ths_questionnaire_title")}
            </Button>
          </Col>
        )}
        {questionnaireLinkVisible && (
          <Col className="gutter-row">
            <Button
              type="primary"
              onClick={() =>
                window.open(treuhandstelleData.questionnaireLink, "_blank")
              }
              disabled={treuhandstelleData.questionnaireLink === null}
            >
              {t("data_control.open_ths_inverview")}
            </Button>
          </Col>
        )}
        <Col className="gutter-row">
          <Button
            type="primary"
            onClick={openSendAkbConfirmationModal}
            disabled={treuhandstelleData.declinedParticipation}
          >
            {t("data_control.send_akb_letter")}
          </Button>
        </Col>
      </Row>
      <Row style={{ marginTop: 20 }}>
        <Col span={24}>
          <Descriptions
            title={
              <Row align="middle" gutter={20}>
                <Col>
                  <span className="ant-descriptions-title">
                    {t("data_control.treuhandstelle_title")}
                  </span>
                </Col>
                <Col>
                  <Popover
                    content={t("masterdata.lw_details.edit_treuhaender_infos")}
                  >
                    <Button
                      type="ghost"
                      icon={<EditTwoTone twoToneColor="#e30613" />}
                      onClick={openTHsFormModal}
                    />
                  </Popover>
                </Col>
              </Row>
            }
            extra={
              actionsVisible && (
                <Space>
                  <Checkbox
                    checked={useMailOnly}
                    onChange={(e) => handleUseMailOnly(e)}
                  >
                    {t("data_control.use_mail_only")}
                  </Checkbox>
                  <Checkbox
                    checked={useThsMailForEmployees}
                    onChange={(e) => {
                      setUseThsMailForEmployees(e.target.checked);
                      handleChangeThsPreferences({
                        useMailOnly,
                        useThsMailForEmployees: e.target.checked,
                        useAllowDuplicateIban,
                      });
                    }}
                  >
                    {t("data_control.use_ths_mail_for_employees")}
                  </Checkbox>
                  <Checkbox
                    checked={useAllowDuplicateIban}
                    onChange={(e) => {
                      setUseAllowDuplicateIban(e.target.checked);
                      handleChangeThsPreferences({
                        useMailOnly,
                        useThsMailForEmployees,
                        useAllowDuplicateIban: e.target.checked,
                      });
                    }}
                  >
                    {t("data_control.allow_duplicate_iban")}
                  </Checkbox>
                </Space>
              )
            }
          >
            <Descriptions.Item label={t("masterdata.lw_details.ths_number")}>
              {treuhandstelleData?.extId}
            </Descriptions.Item>
            <Descriptions.Item label={t("masterdata.lw_details.name")}>
              {treuhandstelleData?.name}
            </Descriptions.Item>
            <Descriptions.Item label={t("masterdata.lw_details.address")}>
              {treuhandstelleData?.address}
              {treuhandstelleData?.addressAddition}
            </Descriptions.Item>
            <Descriptions.Item label={t("masterdata.lw_details.ort")}>
              {treuhandstelleData?.postalCode} {treuhandstelleData?.place}
            </Descriptions.Item>
            <Descriptions.Item label={t("masterdata.lw_details.email")}>
              {treuhandstelleData?.email || "-"}
            </Descriptions.Item>
            <Descriptions.Item label={t("masterdata.lw_details.phone")}>
              {treuhandstelleData?.phone || "-"}
            </Descriptions.Item>
            <Descriptions.Item label={t("masterdata.lw_details.language")}>
              {!isNil(treuhandstelleData?.language) &&
                t(
                  `masterdata.enums.LanguageEnum.${treuhandstelleData?.language}`
                )}
            </Descriptions.Item>
            <Descriptions.Item
              label={t("masterdata.lw_details.treuhaender_contact_email")}
            >
              {treuhandstelleData?.contactTreuhaenderEmail || "-"}
            </Descriptions.Item>
            <Descriptions.Item label={t("masterdata.lw_details.cati_comment")}>
              <pre>{treuhandstelleData?.catiComment || "-"}</pre>
            </Descriptions.Item>
            <Descriptions.Item label={t("data_control.ths_declined")}>
              {treuhandstelleData
                ? t(`${treuhandstelleData.declinedParticipation}`)
                : "-"}
            </Descriptions.Item>
          </Descriptions>
        </Col>
        <Divider />
        <Col span={24}>
          <DataTable<LandwirtMasterdata & { id: number }>
            title={<p className="ant-descriptions-title">Zugeteilte TH & LW</p>}
            isLoading={isLoading || isFetching}
            actions={[]}
            headers={[
              {
                title: t("masterdata.table.labels.th_nummer"),
                key: "treuhaenderId",
                type: ColumnType.Text,
                withSearchFilter: true,
                width: "10%",
                onCell: (row) => ({
                  onClick: () =>
                    handleTHNummerClick(row.treuhaenderId, row.kundennummer),
                }),
              },
              {
                title: t("masterdata.table.labels.firstname"),
                key: "firstname",
                type: ColumnType.Text,
                withSearchFilter: true,
                width: "10%",
                onCell: (row) => ({
                  onClick: () =>
                    handleTHNummerClick(row.treuhaenderId, row.kundennummer),
                }),
              },
              {
                title: t("masterdata.table.labels.lastname"),
                key: "lastname",
                type: ColumnType.Text,
                withSearchFilter: true,
                width: "10%",
                onCell: (row) => ({
                  onClick: () =>
                    handleTHNummerClick(row.treuhaenderId, row.kundennummer),
                }),
              },
              {
                title: t("masterdata.table.labels.kundennummer"),
                key: "kundennummer",
                type: ColumnType.Text,
                withSearchFilter: true,
                width: "10%",
                customFormater: (value, row) =>
                  formatToStringEmptyIfNoKundennummer(value?.toString(), row),
                onCell: (row) => ({
                  onClick: () => goToLWDetailsModal(row.kundennummer),
                }),
              },
              {
                title: t("masterdata.table.labels.rekStatus"),
                key: "rekStatus",
                type: ColumnType.Text,
                customFormater: (value, row) =>
                  formatToStringEmptyIfNoKundennummer(
                    t(`masterdata.enums.RekStatusEnum.${value}`),
                    row
                  ),
                onFilter: getOnFilter("rekStatus"),
                filters: rekStatusFilters,
                width: "10%",
              },
              {
                title: t("masterdata.table.labels.dlStatus"),
                key: "dlStatus",
                type: ColumnType.Text,
                withSearchFilter: true,
                width: "10%",
              },
              {
                title: t("masterdata.table.labels.entschaedigung"),
                key: "entschaedigung",
                type: ColumnType.Boolean,
                width: "10%",
              },
              {
                title: t("masterdata.table.labels.brm"),
                key: "brm",
                type: ColumnType.Boolean,
              },
            ]}
            data={data?.map((item, id) => ({ ...item, id }))}
            isHorizontalScrollDisabled
          />
        </Col>
        <UpdateTHsForm
          initialValues={{
            id: treuhandstelleData.id,
            extId: treuhandstelleData.extId,
            name: treuhandstelleData.name,
            email: treuhandstelleData.email,
            phone: treuhandstelleData.phone,
            language: treuhandstelleData.language,
            place: treuhandstelleData.place,
            address: treuhandstelleData.address,
            postalCode: treuhandstelleData.postalCode,
            catiComment: treuhandstelleData.catiComment,
            iban: treuhandstelleData.iban,
            ibanName: treuhandstelleData.ibanName,
            ibanAddress: treuhandstelleData.ibanAddress,
            ibanZipCode: treuhandstelleData.ibanZipCode,
            ibanTown: treuhandstelleData.ibanTown,
            ibanComment: treuhandstelleData.ibanComment,
            ibanOption: treuhandstelleData.ibanOption,
          }}
          visible={tHsFormVisible}
          onClose={() => setTHsFormVisible(false)}
          onSuccess={() => {
            refetchTreuhandstelleData();
            refetch();
          }}
        />
        <AddTHForm
          treuhandstelleId={treuhandstelleData.id}
          visible={addTHFormVisible}
          onClose={() => setAddTHFormVisible(false)}
          onSuccess={() => {
            refetchTreuhandstelleData();
            refetch();
          }}
        />
      </Row>
      <THInfoModal
        visible={tHInfoModalProps.visible}
        kundennummer={tHInfoModalProps.kundennummer}
        treuhaenderId={tHInfoModalProps.treuhaenderId}
        rekStatusFilters={rekStatusFilters || []}
        onClose={() => {
          setTHInfoModalProps({
            visible: false,
            treuhaenderId: -1,
            kundennummer: -1,
          });
          refetchTreuhandstelleData();
          refetch();
        }}
        tHsInfoModalOnClose={onClose}
      />
      {contactTreuhaenderModalVisible && data && (
        <ContactTreuhaenderModal
          initialValue={treuhandstelleData.contactTreuhaenderId}
          visible={contactTreuhaenderModalVisible}
          data={data}
          onClose={() => setContactTreuhaenderModalVisible(false)}
          useThsMailForEmployees={useThsMailForEmployees}
          treuhandstelleId={treuhandstelleData.id}
          refetchTreuhandstelleData={refetchTreuhandstelleData}
        />
      )}
      <ConfirmationModal
        visible={confirmationModalVisible}
        isLoading={isLoading}
        title={t("data_control.statusversand_confirmation_title").replace(
          "{0}",
          treuhandstelleData.contactTreuhaenderEmail
        )}
        description={t("data_control.statusversand_confiramtion_text").replace(
          "{0}",
          treuhandstelleData.contactTreuhaenderEmail
        )}
        onClose={() => setConfirmationModalVisible(false)}
        onConfirm={() => confirmSendStatusMail()}
      />
      <ConfirmationModal
        visible={thsDeclinedConfirmationModalVisible}
        isLoading={isLoading}
        title={t("data_control.ths_declined_modal_title")}
        description={t("data_control.ths_declined_modal_text")}
        onClose={() => setThsDeclinedConfirmationModalVisible(false)}
        onConfirm={() => confirmSetThsDeclined()}
      />
      <ConfirmationModal
        visible={deleteThsConfirmationModalVisible}
        isLoading={isLoading}
        title={t("data_control.delete_ths_modal_title")}
        description={t("data_control.delete_ths_modal_text")}
        onClose={() => setDeleteThsConfirmationModalVisible(false)}
        onConfirm={() => confirmDeleteThs()}
      />
      <ConfirmationModal
        visible={sendAkbConfirmationModalVisible}
        isLoading={isLoading}
        title={t("data_control.send_akb_letter_confirmation_title")}
        description={t("data_control.send_akb_letter_confirmation_text")}
        onClose={() => setSendAkbConfirmationModalVisible(false)}
        onConfirm={() => confirmSendAkb()}
      />
      <ConfirmationModal
        visible={activateThsQuestionnaireModalVisible}
        isLoading={isLoading}
        title={t("data_control.activate_ths_questionnaire_title")}
        description={t("data_control.activate_ths_questionnaire_confirmation")}
        onClose={() => setActivateThsQuestionnaireModalVisible(false)}
        onConfirm={() => activateThsQuestionnaire()}
      />
    </Modal>
  );
}

export default THsInfoModal;
